import { type EngineContext } from '../core/EngineContext';
import { type ComponentType } from '../entities/ComponentType';
import { Entity } from '../entities/Entity';
import { toEntityId, type EntityId } from '../entities/EntityId';
import { type Camera } from '../rendering/Camera';
import { OrthographicCamera } from '../rendering/OrthographicCamera';
import { EntityIndex } from './EntityIndex';

const FIRST_ENTITY_ID = 1;

/** Construction options for a {@link Scene}. */
export interface SceneOptions {
  /** Label shown in the editor and in error messages. */
  readonly name?: string;
  /** Camera the scene renders through. A centered {@link OrthographicCamera} is created otherwise. */
  readonly camera?: Camera;
}

/**
 * One self-contained world: an entity registry, the indices that make queries cheap, and a camera.
 *
 * A scene is built, not subclassed. A game's `createScene` receives the {@link EngineContext}, creates
 * entities and returns the scene, which keeps scene setup ordinary code rather than a lifecycle to
 * learn.
 *
 * Every query returns a fresh array, so creating or destroying entities while iterating one is safe.
 */
export class Scene {
  /** Label shown in the editor and in error messages. */
  readonly name: string;
  /** Engine services available to everything in this scene. */
  readonly context: EngineContext;
  /** Camera this scene renders through. */
  readonly camera: Camera;

  readonly #entities = new Map<EntityId, Entity>();
  readonly #entitiesByComponent = new EntityIndex<ComponentType>();
  readonly #entitiesByTag = new EntityIndex<string>();

  #nextEntityId = FIRST_ENTITY_ID;

  constructor(context: EngineContext, options: SceneOptions = {}) {
    this.context = context;
    this.name = options.name ?? 'Scene';
    this.camera = options.camera ?? new OrthographicCamera();
  }

  /** Number of live entities. */
  get entityCount(): number {
    return this.#entities.size;
  }

  /** Every live entity in creation order, as a fresh array. */
  get entities(): readonly Entity[] {
    return [...this.#entities.values()];
  }

  /** Creates an entity at the scene root and registers it. */
  createEntity(name = 'Entity'): Entity {
    const id = toEntityId(this.#nextEntityId);
    this.#nextEntityId += 1;

    const entity = new Entity(this, id, name);
    this.#entities.set(id, entity);
    this.context.events.emit('entityCreated', { entityId: id });
    return entity;
  }

  /**
   * Resolves an id to its entity, or undefined when the entity no longer exists.
   *
   * Ids are never reused, so a stale id can only ever return undefined; it can never resolve to some
   * unrelated entity that happened to take the slot.
   */
  getEntity(id: EntityId): Entity | undefined {
    return this.#entities.get(id);
  }

  /** Every entity carrying a component of exactly this type, as a fresh array in creation order. */
  getEntitiesWith<TComponent extends object>(type: ComponentType<TComponent>): readonly Entity[] {
    return this.#entitiesByComponent.get(type);
  }

  /** Every entity carrying the given tag, as a fresh array in creation order. */
  getEntitiesByTag(tag: string): readonly Entity[] {
    return this.#entitiesByTag.get(tag);
  }

  /** The first entity with this name, or undefined. Names are not unique; prefer ids and tags. */
  findEntityByName(name: string): Entity | undefined {
    for (const entity of this.#entities.values()) {
      if (entity.name === name) {
        return entity;
      }
    }
    return undefined;
  }

  /** Destroys the entity with this id, along with its subtree, and reports whether it existed. */
  destroyEntity(id: EntityId): boolean {
    const entity = this.#entities.get(id);
    if (entity === undefined) {
      return false;
    }
    entity.destroy();
    return true;
  }

  /** Destroys every entity in the scene, leaving it reusable and empty. */
  clear(): void {
    for (const entity of this.entities) {
      entity.destroy();
    }
  }

  /** @internal Records that an entity gained a component. Called by {@link Entity.addComponent}. */
  indexComponent(entity: Entity, type: ComponentType): void {
    this.#entitiesByComponent.add(type, entity);
  }

  /** @internal Records that an entity lost a component. Called by {@link Entity.removeComponent}. */
  unindexComponent(entity: Entity, type: ComponentType): void {
    this.#entitiesByComponent.remove(type, entity);
  }

  /** @internal Records that an entity gained a tag. Called by {@link Entity.addTag}. */
  indexTag(entity: Entity, tag: string): void {
    this.#entitiesByTag.add(tag, entity);
  }

  /** @internal Records that an entity lost a tag. Called by {@link Entity.removeTag}. */
  unindexTag(entity: Entity, tag: string): void {
    this.#entitiesByTag.remove(tag, entity);
  }

  /** @internal Drops an entity from the registry and every index. Called by {@link Entity.destroy}. */
  releaseEntity(entity: Entity): void {
    if (!this.#entities.delete(entity.id)) {
      return;
    }

    for (const type of entity.components.map((component) => component.constructor as ComponentType)) {
      this.unindexComponent(entity, type);
    }
    for (const tag of entity.tags) {
      this.unindexTag(entity, tag);
    }
    this.context.events.emit('entityDestroyed', { entityId: entity.id });
  }
}
